import React, { Fragment, useEffect, useState } from 'react';
import Footer from '../Footer/Footer';
import Loader from '../../loader/Loader';
import PageSEO from '../Common/PageSEO';
import {
    FeatureHero,
    FeatureHeroCta,
    FeatureSection,
    FeatureDownloadCta
} from '../Common/FeatureContent';
import { useLocale } from '../../localization/LocaleContext';
import { ROUTE_PATHS } from '../../localization/constants';
import './PaymentRemindersMain.css';

/* Inline SVG icons (no FontAwesome dependency) */

const SEND_ICON = (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13"/>
        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
);

const CLOCK_ICON = (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="1 4 1 10 7 10"/>
        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
        <polyline points="12 7 12 12 15 14"/>
    </svg>
);

const BELL_ICON = (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
);

const CHECK_CIRCLE_ICON = (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
);

const ENVELOPE_ICON = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2"/>
        <polyline points="22 6 12 13 2 6"/>
    </svg>
);

const MOBILE_ICON = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
        <line x1="12" y1="18" x2="12.01" y2="18"/>
    </svg>
);

const BOLT_ICON = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
);

const QUOTE_ICON = (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M6 17h3l2-4V7H5v6h3l-2 4zm8 0h3l2-4V7h-6v6h3l-2 4z"/>
    </svg>
);

const ARROW_DOWN_ICON = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/>
        <polyline points="19 12 12 19 5 12"/>
    </svg>
);

const SIGNAL_ICON = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M2 20h3v-6H2v6zm5 0h3V9H7v11zm5 0h3V5h-3v15zm5 0h3V2h-3v18z"/>
    </svg>
);

const WIFI_ICON = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 21l3.5-4.5a5.5 5.5 0 0 0-7 0L12 21zm0-9c2.5 0 4.8 1 6.5 2.6l1.5-2A12 12 0 0 0 12 9c-3 0-5.8 1-8 2.6l1.5 2A9.9 9.9 0 0 1 12 12zm0-5c3.9 0 7.4 1.5 10 4l1.5-2A17 17 0 0 0 12 4C7.6 4 3.6 5.7.5 8.5l1.5 2A14.9 14.9 0 0 1 12 7z"/>
    </svg>
);

const BATTERY_ICON = (
    <svg width="16" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <rect x="1" y="6" width="18" height="12" rx="2"/>
        <rect x="20" y="9" width="2" height="6" rx="1"/>
    </svg>
);

const APP_ICON = (
    <img
        className="pr-push-app-icon-img"
        src={`${process.env.PUBLIC_URL}/images/app-icon.png`}
        alt=""
        aria-hidden="true"
    />
);

function PaymentRemindersMain(){
    const { t, locale } = useLocale();
    const p = (key) => t(`featurePages.paymentReminders.${key}`);

    const[loader, SetLoader] = useState(false)

    useEffect(() => {
        const timer = setTimeout(() => {
            SetLoader(true)
        }, 1000)

        return () => clearTimeout(timer)
    }, [])

    if (!loader) {
        return <Loader/>;
    }

    const flowSteps = [
        { label: p('chainStep1'), icon: SEND_ICON },
        { label: p('chainStep2'), icon: CLOCK_ICON },
        { label: p('chainStep3'), icon: BELL_ICON, active: true },
        { label: p('chainStep4'), icon: CHECK_CIRCLE_ICON }
    ];

    return(
        <Fragment>
            <PageSEO title={p('metaTitle')} description={p('metaDescription')} path={ROUTE_PATHS.paymentReminders} locale={locale} />

            <FeatureHero
                className="pr-hero"
                eyebrow={p('eyebrow')}
                title={p('heroTitle')}
                highlight={p('heroHighlight')}
                subtitle={p('heroSubtitle')}
                cta={
                    <FeatureHeroCta
                        primaryLabel={t('shared.featureStartFree')}
                        primaryTarget="feature-download"
                        secondaryLabel={t('shared.featureSeeHowItWorks')}
                        secondaryTarget="pr-notifications"
                    />
                }
            />

            {/* Automated workflow */}
            <FeatureSection className="pr-flow-section" title={p('sectionTitle')} description={p('sectionDescription')}>
                <div className="pr-flow">
                    <div className="pr-flow-line" aria-hidden="true"></div>
                    <div className="pr-flow-steps">
                        {flowSteps.map((step, idx) => (
                            <Fragment key={idx}>
                                <div className={`pr-flow-step${step.active ? ' pr-flow-step-active' : ''}`}>
                                    <div className="pr-flow-icon">
                                        {step.active && <span className="pr-flow-ping" aria-hidden="true"></span>}
                                        {step.icon}
                                    </div>
                                    <span className="pr-flow-label">{step.label}</span>
                                </div>
                                {idx < flowSteps.length - 1 && (
                                    <div className="pr-flow-arrow" aria-hidden="true">{ARROW_DOWN_ICON}</div>
                                )}
                            </Fragment>
                        ))}
                    </div>
                </div>
            </FeatureSection>

            {/* Push & email notifications */}
            <section id="pr-notifications" className="pr-notify-section">
                <div className="container">
                    <div className="fc-section-header">
                        <span className="pr-eyebrow">{BOLT_ICON}<span>{p('notifyEyebrow')}</span></span>
                        <h2 className="h-1 h-bold fc-section-title">{p('notifyTitle')}</h2>
                        <p className="fc-section-description">{p('notifyDescription')}</p>
                    </div>

                    <div className="pr-notify-grid">
                        {/* Left: client email mockup */}
                        <div className="pr-notify-col">
                            <h3 className="pr-notify-heading">{ENVELOPE_ICON}<span>{p('clientHeading')}</span></h3>
                            <p className="pr-notify-sub">{p('clientSubtitle')}</p>

                            <div className="pr-email-card">
                                <div className="pr-email-chrome">
                                    <span className="pr-dot pr-dot-rose"></span>
                                    <span className="pr-dot pr-dot-amber"></span>
                                    <span className="pr-dot pr-dot-emerald"></span>
                                </div>
                                <div className="pr-email-body">
                                    <div className="pr-email-top">
                                        <div>
                                            <h4 className="pr-email-subject">{p('emailSubject')}</h4>
                                            <p className="pr-email-from">{p('emailFrom')}</p>
                                        </div>
                                        <span className="pr-email-time">{p('emailTime')}</span>
                                    </div>
                                    <div className="pr-email-inner">
                                        <p className="pr-email-line">{p('emailGreeting')}</p>
                                        <p className="pr-email-line">{p('emailBody')}</p>
                                        <div className="pr-email-amount">
                                            <div>
                                                <p className="pr-email-amount-label">{p('emailAmountLabel')}</p>
                                                <p className="pr-email-amount-value">{p('emailAmountValue')}</p>
                                            </div>
                                            <span className="pr-email-pay-btn">{p('emailPayButton')}</span>
                                        </div>
                                        <p className="pr-email-thanks">{p('emailThanks')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right: push notification mockup */}
                        <div className="pr-notify-col">
                            <h3 className="pr-notify-heading">{MOBILE_ICON}<span>{p('youHeading')}</span></h3>
                            <p className="pr-notify-sub">{p('youSubtitle')}</p>

                            <div className="pr-phone">
                                <div className="pr-phone-notch" aria-hidden="true"></div>
                                <div className="pr-phone-screen">
                                    <div className="pr-phone-status">
                                        <span>{p('phoneTime')}</span>
                                        <div className="pr-phone-status-icons">
                                            {SIGNAL_ICON}{WIFI_ICON}{BATTERY_ICON}
                                        </div>
                                    </div>
                                    <div className="pr-push-stack">
                                        <div className="pr-push pr-push-primary">
                                            <div className="pr-push-head">
                                                <span className="pr-push-app-icon">{APP_ICON}</span>
                                                <span className="pr-push-app">{p('pushAppName')}</span>
                                                <span className="pr-push-time">{p('push1Time')}</span>
                                            </div>
                                            <p className="pr-push-title">{p('push1Title')}</p>
                                            <p className="pr-push-body">{p('push1Body')}</p>
                                        </div>
                                        <div className="pr-push pr-push-secondary">
                                            <div className="pr-push-head">
                                                <span className="pr-push-app-icon">{APP_ICON}</span>
                                                <span className="pr-push-app">{p('pushAppName')}</span>
                                                <span className="pr-push-time">{p('push2Time')}</span>
                                            </div>
                                            <p className="pr-push-title">{p('push2Title')}</p>
                                            <p className="pr-push-body">{p('push2Body')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Philosophy quote */}
            <section className="pr-quote-section">
                <div className="container">
                    <span className="pr-quote-mark" aria-hidden="true">{QUOTE_ICON}</span>
                    <p className="pr-quote-text">{p('quote')}</p>
                    <div className="pr-quote-author">
                        <span className="pr-quote-avatar">IP</span>
                        <div>
                            <p className="pr-quote-name">{p('quoteAuthor')}</p>
                            <p className="pr-quote-role">{p('quoteRole')}</p>
                        </div>
                    </div>
                </div>
            </section>

            <FeatureDownloadCta
                className="pr-download-cta"
                title={p('ctaTitle')}
                description={p('ctaDescription')}
                ctaLabel={p('ctaButton')}
                storesLabel={t('shared.featureDownloadApp')}
            />

            <Footer />
            </Fragment>
    )
}

export default PaymentRemindersMain;